import { Logger, namespace, Point } from '../../../../../FrontendBundle/Resources/js/ofcs';
import AbstractItem from './abstractItem';
import ResizeCircleHandle from '../svg/handle/resizeCircleHandle';

namespace('Ofcs.Editor.Item');

/**
 * @class CircleItem
 * @extends AbstractItem
 */
export default window.Ofcs.Editor.Item.CircleItem = AbstractItem.extend({

    type: 'circle',

    /**
     * Constant for minimum diameter of a circle bag
     *
     * @constant
     * @property {number} MIN_SIZE
     */
    MIN_SIZE: 5,

    /**
     * Triggered when the segments are updated
     *
     * @event updatesegments
     * @param {CircleItem} item Me
     */

    defaults: {
        /**
         * @property {string} Type
         */
        type: 'Circle',

        /**
         * Current position
         * @property {Point} attributes.position
         */
        position: new Point(0, 0),

        /**
         * Width of the shape
         * @property {number} attributes.width
         */
        width: 0,

        /**
         * Height of the shape
         * @property {number} attributes.height
         */
        height: 0,

        /**
         * Depth of the circle bag
         * @property {number} attributes.depth
         */
        depth: 10,

        /**
         * Label
         * @property {string} attributes.userLabel
         */
        userLabel: '',

        /**
         * Rotation of the shape
         * @property {number} attributes.rotation
         */
        rotation: 0,
    },

    initialize() {
        AbstractItem.prototype.initialize.call(this);

        this.supportsWidth = true;
        this.supportsPositioning = true;
        this.supportsDepth = true;
        this.supportsCollisions = true;
        this.supportsOutOfFoam = true;
        this.supportsGuides = true;

        // Initialize paths
        this.set('ext', this.calculateSegments(0));
        this.set('orig', this.get('ext'));
        this.set('safety', this.calculateSegments(this.getSafetyMargin()));

        this.on('change', this.onItemChange, this);
    },

    onItemChange(item) {
        const spread = this.getSafetyMargin();

        if (item.hasChangedProperty('width', 'height')) {
            this.set('ext', this.calculateSegments(0));
            this.set('orig', this.get('ext'));
        }

        if (item.hasChangedProperty('width', 'height', 'depth')) {
            this.set('safety', this.calculateSegments(spread));
            this.trigger('updatesegments', this);
        }
    },

    calculateSegments(spread) {
        const radius = (this.get('height') / 2) + (spread);
        const path = [];
        const step = Math.max(1, Math.min(10, Math.round(720 / (radius))));

        for (let angle = 0; angle < 360; angle += step) {
            const x = radius * Math.cos((angle * Math.PI) / 180);
            const y = radius * Math.sin((angle * Math.PI) / 180);

            if (!angle) {
                path.push(['M', x, y]);
            } else {
                path.push(['L', x, y]);
            }
        }

        path.push(['L', radius, 0]);

        return [path];
    },

    set(key, value, optionsParam) {
        const { attrs, options } = this._normalizeSetParameters(key, value, optionsParam);

        if (attrs.width !== undefined) {
            attrs.width = Math.max(this.MIN_SIZE, attrs.width);
            Logger.debug('circle: fixed width', attrs.width);

            if (attrs.height === undefined || attrs.height === this.attributes.height) {
                attrs.height = attrs.width;
            }
        }

        if (attrs.height !== undefined) {
            attrs.height = Math.max(this.MIN_SIZE, attrs.height);
            Logger.debug('circle: fixed height', attrs.height);

            if (attrs.width === undefined || attrs.width === this.attributes.width) {
                attrs.width = attrs.height;
            }
        }

        if (attrs.depth !== undefined) {
            attrs.depth = Math.max(this.getMinDepth(), attrs.depth);
            attrs.depth = Math.min(this.getMaxDepth(), attrs.depth);
            Logger.debug('circle: fixed depth', attrs.depth);
        }

        return AbstractItem.prototype.set.call(this, attrs, options);
    },

    /**
     * Get radius.
     *
     * @return {number} radius
     */
    getRadius() {
        return this.get('height') / 2;
    },

    /**
     * Get top point.
     *
     * @return {Point}
     */
    getPointTop() {
        const center = this.get('position');
        const radius = this.get('height') / 2;
        return new Point(center.x, center.y - radius);
    },

    /**
     * Get bottom point.
     *
     * @return {Point}
     */
    getPointBottom() {
        const center = this.get('position');
        const radius = this.get('height') / 2;
        return new Point(center.x, center.y + radius);
    },

    /**
     * Get left point.
     *
     * @return {Point}
     */
    getPointLeft() {
        const center = this.get('position');
        const radius = this.get('width') / 2;
        return new Point(center.x - radius, center.y);
    },

    /**
     * Get right point.
     *
     * @return {Point}
     */
    getPointRight() {
        const center = this.get('position');
        const radius = this.get('width') / 2;
        return new Point(center.x + radius, center.y);
    },

    /**
     * Get minimum depth defined in parent item.
     *
     * @return {number}
     */
    getMinDepth() {
        const parentItem = this.getParentItem();

        return parentItem ? parentItem.get('minDepth') : 0;
    },

    /**
     * Get minimum with.
     *
     * @return {number}
     */
    getMinWidth() {
        return this.MIN_SIZE;
    },

    /**
     * Get minimum height.
     *
     * @return {number}
     */
    getMinHeight() {
        return this.MIN_SIZE;
    },

    /**
     * Get maximum depth defined in parent item.
     *
     * @return {number}
     */
    getMaxDepth() {
        const parentItem = this.getParentItem();

        return parentItem ? parentItem.get('depth') : 9999;
    },

    /**
     * Translate object into json stringable types. This method will be used
     * by JSON.stringify() automaticly.
     *
     * @return {Object}
     */
    toJSON(...args) {
        const attributes = AbstractItem.prototype.toJSON.apply(this, args);

        return {
            depth: attributes.depth,
            x: attributes.x,
            y: attributes.y,
            width: attributes.width,
            height: attributes.height,
            type: attributes.type,
        };
    },

    /**
     * Initializes item handles
     *
     * @param {Ofcs.Editor.Svg.Canvas} canvas - svg canvas instance
     * @return {ResizeCircleHandle}
     */
    initializeHandles(canvas) {
        return new ResizeCircleHandle(this, canvas);
    },
});
